<template>
    <div class="module-settings">
        <div v-if="loading" class="loading-wrapper">
            <a-skeleton :active="true" :paragraph="{ rows: 5 }" title />
        </div>
        <div v-else-if="currentModule" class="route-page-content">
            <SidebarChild
                :items="menuItems"
                :focusActive="true"
                @selected="handleSidebarSelection"
                :selectedItem="selectedItem"
            />
            <div class="module-settings-page-content">
                <div class="title-wrapper">
                    <i class="material-icons">{{ currentModule.icon || "extension" }}</i>
                    <h3 class="title">{{ $t("modules.settings") }} - {{ currentModule.displayName }}</h3>
                </div>

                <a-form :form="form" @submit.prevent="handleSubmit" layout="vertical" class="module-settings-form">
                    <a-form-item :label="$t('modules.displayName')">
                        <a-input v-model="form.displayName" />
                    </a-form-item>

                    <a-form-item :label="$t('modules.description')">
                        <a-textarea v-model="form.description" />
                    </a-form-item>

                    <a-form-item :label="$t('modules.type')">
                        <a-select v-model="form.type">
                            <a-select-option value="module">Module</a-select-option>
                            <a-select-option value="relation">Relation</a-select-option>
                        </a-select>
                    </a-form-item>

                    <!-- Module type settings -->
                    <template v-if="form.type === 'module'">
                        <a-form-item :label="$t('modules.table')">
                            <a-select v-model="form.module.table" :allowClear="true" placeholder="Tablo Seçiniz">
                                <a-select-option v-for="table in tables" :key="table._id" :value="table._id">
                                    {{ table.name }}
                                </a-select-option>
                            </a-select>
                        </a-form-item>

                        <a-form-item :label="$t('modules.menuApiSettings')">
                            <a-checkbox v-model="form.module.settings.showInMainMenu">
                                {{ $t('modules.showInMainMenu') }}
                            </a-checkbox>
                            <a-checkbox v-model="form.module.settings.apiEnabled">
                                {{ $t('modules.apiEnabled') }}
                            </a-checkbox>
                        </a-form-item>
                    </template>

                    <!-- Relation type settings -->
                    <template v-if="form.type === 'relation'">
                        <a-form-item :label="$t('modules.relationType')">
                            <a-select v-model="form.relation.type">
                                <a-select-option value="table-to-table">Tablo - Tablo</a-select-option>
                                <a-select-option value="table-to-standart">Tablo - Standart</a-select-option>
                                <a-select-option value="standart-to-table">Standart - Tablo</a-select-option>
                            </a-select>
                        </a-form-item>

                        <template v-if="form.relation.type === 'table-to-table'">
                            <!-- From Table Settings -->
                            <a-form-item :label="$t('modules.fromTable')">
                                <a-select v-model="form.relation.tableToTable.from.table">
                                    <a-select-option v-for="table in tables" :key="table._id" :value="table._id">
                                        {{ table.name }}
                                    </a-select-option>
                                </a-select>
                            </a-form-item>

                            <a-form-item :label="$t('modules.fromConnectionType')">
                                <a-select v-model="form.relation.tableToTable.from.connectionType">
                                    <a-select-option value="_id">ID</a-select-option>
                                    <a-select-option value="field">Alan</a-select-option>
                                </a-select>
                            </a-form-item>

                            <!-- To Table Settings -->
                            <a-form-item :label="$t('modules.toTable')">
                                <a-select v-model="form.relation.tableToTable.to.table">
                                    <a-select-option v-for="table in tables" :key="table._id" :value="table._id">
                                        {{ table.name }}
                                    </a-select-option>
                                </a-select>
                            </a-form-item>

                            <a-form-item :label="$t('modules.toConnectionType')">
                                <a-select v-model="form.relation.tableToTable.to.connectionType">
                                    <a-select-option value="_id">ID</a-select-option>
                                    <a-select-option value="field">Alan</a-select-option>
                                </a-select>
                            </a-form-item>
                        </template>
                    </template>

                    <a-form-item :label="$t('modules.icon')">
                        <MaterialIconPicker v-model="form.icon" />
                    </a-form-item>

                    <a-form-item>
                        <a-button type="primary" html-type="submit">{{ $t('modules.save') }}</a-button>
                    </a-form-item>
                </a-form>
            </div>
        </div>
        <div v-else class="not-found">
            <h3>Modül bulunamadı.</h3>
        </div>
    </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import SidebarChild from "../../../components/Sidebar/SidebarChild.vue";
import MaterialIconPicker from "./MaterialIconPicker.vue";

export default {
    name: "ModuleSettings",
    components: {
        SidebarChild,
        MaterialIconPicker,
    },
    data() {
        return {
            loading: true,
            currentModule: null,
            selectedItem: "module-settings",
            form: {
                displayName: "",
                description: "",
                type: "module",
                module: {
                    table: null,
                    settings: {
                        showInMainMenu: true,
                        apiEnabled: true,
                    }
                },
                relation: {
                    type: "table-to-table",
                    tableToTable: {
                        from: {
                            table: null,
                            connectionType: "_id",
                            field: null
                        },
                        to: {
                            table: null,
                            connectionType: "field",
                            field: null
                        }
                    }
                },
                icon: "",
            },
        };
    },
    computed: {
        ...mapState({
            tables: (state) => state.table.tables,
            modules: (state) => state.module.modules,
        }),
        menuItems() {
            return [
                {
                    enabled: true,
                    key: "list",
                    label: this.currentModule?.displayName || "List",
                    route: `/module/${this.$route.params.moduleName}`,
                    icon: {
                        type: "material-icons",
                        name: "list",
                    },
                },
                {
                    enabled: true,
                    key: "module-settings",
                    label: this.$t("modules.modulesSettings"),
                    route: `/module/${this.$route.params.moduleName}/settings`,
                    icon: {
                        type: "material-icons",
                        name: "settings",
                    },
                },
            ];
        },
    },
    methods: {
        ...mapActions("module", ["syncModules", "updateModule"]),

        findModuleByName(moduleName) {
            return this.modules.find((m) => m.moduleName === moduleName);
        },
        handleSidebarSelection(item) {
            this.selectedItem = item.key;
            this.$router.push(item.route);
        },

        searchModules(query) {
            if (!query) {
                return this.modules;
            }
            const lowerCaseQuery = query.toLowerCase();
            return this.modules.filter(module => 
                module.displayName.toLowerCase().includes(lowerCaseQuery) ||
                module.description.toLowerCase().includes(lowerCaseQuery)
            );
        },

        async loadModule() {
            try {
                this.loading = true;
                await this.syncModules();

                const moduleName = this.$route.params.moduleName;
                const foundModule = this.findModuleByName(moduleName);

                if (foundModule) {
                    this.currentModule = foundModule;
                    // Form'u mevcut modül bilgileriyle dolduruyoruz
                    this.form.displayName = foundModule.displayName || "";
                    this.form.description = foundModule.description || "";
                    this.form.type = foundModule.type || "module";
                    
                    // Module tipi ayarları
                    if (foundModule.type === "module") {
                        this.form.module = {
                            table: foundModule.module?.table || null,
                            settings: {
                                showInMainMenu: foundModule.module?.settings?.showInMainMenu ?? true,
                                apiEnabled: foundModule.module?.settings?.apiEnabled ?? true,
                            }
                        };
                    }
                    
                    // Relation tipi ayarları
                    if (foundModule.type === "relation") {
                        this.form.relation = {
                            type: foundModule.relation?.type || "table-to-table",
                            tableToTable: foundModule.relation?.tableToTable || {
                                from: {
                                    table: null,
                                    connectionType: "_id",
                                    field: null
                                },
                                to: {
                                    table: null,
                                    connectionType: "field",
                                    field: null
                                }
                            }
                        };
                    }

                    this.form.icon = foundModule.icon || "";
                } else {
                    this.$router.push("/404");
                }
            } catch (error) {
                console.error("loadModule error:", error);
            } finally {
                this.loading = false;
            }
        },

        async handleSubmit() {
            try {
                const moduleName = this.$route.params.moduleName;

                // Modül tipine göre güncellenecek alanları belirle
                const updatingFields = {
                    displayName: this.form.displayName,
                    description: this.form.description,
                    type: this.form.type,
                    icon: this.form.icon,
                };

                // Modül tipine göre ilgili alanları ekle
                if (this.form.type === "module") {
                    updatingFields.module = this.form.module;
                } else if (this.form.type === "relation") {
                    updatingFields.relation = this.form.relation;
                }

                await this.updateModule({ moduleName, updatingFields });
                this.$message.success("Modül başarıyla güncellendi.");
                this.syncModules();
            } catch (error) {
                console.error("handleSubmit error:", error);
                this.$message.error("Modül güncelleme başarısız oldu.");
            }
        },
    },
    created() {
        this.loadModule();
    },
    watch: {
        "$route.params.moduleName": function () {
            this.loadModule();
        },
    },
};
</script>

<style scoped lang="scss">
.module-settings {
    .loading-wrapper {
        padding: 16px;
    }

    .not-found {
        padding: 16px;
        h3 {
            color: #ff4d4f;
        }
    }
    .module-settings-form {
        padding: 16px;
    }
    .module-settings-page-content {
        display: flex;
        flex-direction: column;
        gap: 10px;
        width: 100%;
        .title-wrapper {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 10px;
            h3 {
                margin: 0;
            }
        }
    }
}
</style>
